import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, Zap, MailCheck, Check, X, RefreshCw } from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import Brand from "../components/Brand";

const STRENGTH_RULES = [
  { key: "len", label: "At least 10 characters", test: (p) => p.length >= 10 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "One digit", test: (p) => /\d/.test(p) },
  { key: "special", label: "One special character (!@#$…)", test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(p) },
];

function StrengthChecklist({ password }) {
  if (!password) return null;
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }} data-testid="register-strength-list">
      {STRENGTH_RULES.map((r) => {
        const ok = r.test(password);
        return (
          <li key={r.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", color: ok ? "var(--pf-ok)" : "var(--pf-text-dim)" }} data-testid={`register-strength-${r.key}`} data-ok={ok}>
            {ok ? <Check size={12} /> : <X size={12} />} {r.label}
          </li>
        );
      })}
    </ul>
  );
}

function PendingScreen({ email, onResend, onBackToLogin }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const doResend = async () => {
    setBusy(true); setMsg("");
    const r = await onResend(email);
    setBusy(false);
    setMsg(r.message || (r.ok ? "Sent." : r.error || "Failed."));
  };
  return (
    <div style={{ textAlign: "center" }} data-testid="register-pending">
      <div style={{ display: "inline-flex", width: 56, height: 56, background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)", alignItems: "center", justifyContent: "center", borderRadius: 2, marginBottom: 18 }}>
        <MailCheck size={24} color="var(--pf-spark)" />
      </div>
      <h1 className="pf-heading" style={{ fontSize: 26, margin: "0 0 10px" }}>Check your inbox</h1>
      <p style={{ color: "var(--pf-text-muted)", fontSize: 14, lineHeight: 1.55, margin: "0 0 18px" }}>
        We sent a verification link to <strong style={{ color: "var(--pf-text)" }} data-testid="register-pending-email">{email}</strong>.
        Click the link in the email to activate your account, then sign in.
      </p>
      <button type="button" className="btn btn-ghost" onClick={doResend} disabled={busy} style={{ marginRight: 8 }} data-testid="register-resend">
        {busy ? <span className="pf-spin" /> : <RefreshCw size={13} />} Resend email
      </button>
      <Link to="/login" className="btn btn-primary" data-testid="register-pending-signin" onClick={onBackToLogin}>
        Go to sign in <ArrowRight size={13} />
      </Link>
      {msg && (
        <div className="pf-sev pf-sev-info" style={{ display: "block", marginTop: 16, padding: "8px 10px" }} data-testid="register-resend-msg">{msg}</div>
      )}
    </div>
  );
}

export default function Register() {
  const { user, ready, register, resendVerification } = useAuth();
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [industry, setIndustry] = useState("");
  const [subIndustries, setSubIndustries] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [industryCatalogue, setIndustryCatalogue] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/admin/industries")
      .then((r) => setIndustryCatalogue(r.data || {}))
      .catch(() => setIndustryCatalogue({}));
  }, []);

  // Reset sub-industry selection whenever the industry changes
  useEffect(() => { setSubIndustries([]); }, [industry]);

  const subOptions = useMemo(() => {
    const meta = industryCatalogue[industry];
    return meta ? meta.sub_industries : [];
  }, [industry, industryCatalogue]);

  const toggleSub = (sub) => {
    setSubIndustries((cur) =>
      cur.includes(sub) ? cur.filter((s) => s !== sub) : [...cur, sub]);
  };

  const strengthOk = useMemo(() => STRENGTH_RULES.every((r) => r.test(password)), [password]);
  const match = password && password === confirm;

  if (ready && user && user.id) return <Navigate to="/projects" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim()) { setErr("Please enter your full name."); return; }
    if (!email.trim()) { setErr("Please enter your email address."); return; }
    if (!companyName.trim()) { setErr("Please enter your company name."); return; }
    if (!industry) { setErr("Please select an industry."); return; }
    if (subIndustries.length === 0) {
      setErr("Please select at least one sub-industry."); return;
    }
    if (!password) { setErr("Please choose a password."); return; }
    if (!strengthOk) {
      const missing = STRENGTH_RULES.filter((r) => !r.test(password)).map((r) => r.label);
      setErr(`Password is missing: ${missing.join(", ")}.`); return;
    }
    if (!match) { setErr("Passwords do not match."); return; }
    setBusy(true);
    const r = await register({
      email: email.trim(),
      password,
      confirmPassword: confirm,
      name: name.trim(),
      companyName: companyName.trim(),
      phone: phone.trim() || null,
      industry,
      subIndustries,
      isAdmin,
    });
    setBusy(false);
    if (r.ok && r.pending) {
      setPendingEmail(r.email || email.trim());
      return;
    }
    if (!r.ok && r.created) {
      setPendingEmail(r.email || email.trim());
      setErr(r.error);
      return;
    }
    setErr(r.error || "Registration failed");
  };

  return (
    <div className="pf-blueprint-bg" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div className="pf-card pf-grain" style={{ width: 560, padding: 32, maxHeight: "92vh", overflowY: "auto" }}>
        <Link to="/" className="pf-heading" style={{ display: "inline-flex", alignItems: "center", color: "var(--pf-text)", textDecoration: "none", marginBottom: 22 }}>
          <Brand height={84} withTagline testId="register-brand" />
        </Link>

        {pendingEmail ? (
          <PendingScreen
            email={pendingEmail}
            onResend={resendVerification}
            onBackToLogin={() => nav("/login")}
          />
        ) : (
          <>
            <div className="pf-overline" style={{ marginBottom: 6 }}>NEW ACCOUNT · CREATE PROFILE</div>
            <h1 className="pf-heading" style={{ fontSize: 26, margin: "0 0 22px" }}>Open your engineering workbench</h1>

            <form onSubmit={submit} noValidate>
              <label className="pf-label" htmlFor="rg-name">FULL NAME</label>
              <input id="rg-name" className="pf-input" type="text" required autoComplete="name"
                value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" data-testid="register-name" />

              <div style={{ height: 12 }} />

              <label className="pf-label" htmlFor="rg-email">EMAIL</label>
              <input id="rg-email" className="pf-input" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="register-email" />

              <div style={{ height: 12 }} />

              <label className="pf-label" htmlFor="rg-company">COMPANY NAME</label>
              <input id="rg-company" className="pf-input" type="text" required autoComplete="organization"
                value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Acme Engineering" data-testid="register-company" />

              <div style={{ height: 12 }} />

              <label className="pf-label" htmlFor="rg-phone">PHONE NUMBER</label>
              <input id="rg-phone" className="pf-input" type="tel" autoComplete="tel"
                value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 90000 00000" data-testid="register-phone" />

              <div style={{ height: 12 }} />

              <label className="pf-label" htmlFor="rg-industry">INDUSTRY</label>
              <select id="rg-industry" className="pf-input" required
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      data-testid="register-industry">
                <option value="">Select industry…</option>
                {Object.entries(industryCatalogue).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>

              {industry && (
                <>
                  <div style={{ height: 12 }} />
                  <label className="pf-label">
                    SUB-INDUSTRIES <span style={{ color: "var(--pf-text-muted)", fontWeight: "normal" }}>(select one or more)</span>
                  </label>
                  <div data-testid="register-sub-industries"
                        style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                                  gap: "6px 12px", padding: "8px 10px",
                                  background: "var(--pf-surface-2)",
                                  border: "1px solid var(--pf-border)",
                                  borderRadius: 4, maxHeight: 200, overflowY: "auto" }}>
                    {subOptions.map((sub) => (
                      <label key={sub}
                              data-testid={`register-sub-${sub.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                              style={{ display: "flex", alignItems: "center", gap: 6,
                                        fontSize: 12, cursor: "pointer",
                                        color: "var(--pf-text)",
                                        padding: "3px 0" }}>
                        <input type="checkbox"
                                checked={subIndustries.includes(sub)}
                                onChange={() => toggleSub(sub)} />
                        {sub}
                      </label>
                    ))}
                  </div>
                </>
              )}

              <div style={{ height: 12 }} />

              <label data-testid="register-isadmin-row"
                      style={{ display: "flex", alignItems: "center", gap: 8,
                                fontSize: 12, color: "var(--pf-text)",
                                padding: "8px 10px",
                                background: "var(--pf-surface-2)",
                                border: "1px solid var(--pf-border)",
                                borderRadius: 4, cursor: "pointer" }}>
                <input type="checkbox" checked={isAdmin}
                        onChange={(e) => setIsAdmin(e.target.checked)}
                        data-testid="register-isadmin" />
                Are you the admin? <span style={{ color: "var(--pf-text-muted)" }}>(Yes / No)</span>
              </label>

              <div style={{ height: 12 }} />

              <label className="pf-label" htmlFor="rg-password">PASSWORD</label>
              <input id="rg-password" className="pf-input" type="password" autoComplete="new-password" required minLength={10}
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 10 characters" data-testid="register-password" />
              <StrengthChecklist password={password} />

              <div style={{ height: 12 }} />

              <label className="pf-label" htmlFor="rg-confirm">CONFIRM PASSWORD</label>
              <input id="rg-confirm" className="pf-input" type="password" autoComplete="new-password" required minLength={10}
                value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat your password" data-testid="register-confirm" />
              {confirm && !match && (
                <div style={{ fontSize: 11, color: "var(--pf-err)", marginTop: 6, fontFamily: "'JetBrains Mono', monospace" }} data-testid="register-mismatch">
                  Passwords do not match
                </div>
              )}

              {err && <div className="pf-sev pf-sev-error" style={{ display: "block", marginTop: 14, padding: "8px 10px" }} data-testid="register-error">{err}</div>}

              <button type="submit" className="btn btn-primary" style={{ marginTop: 20, width: "100%" }} disabled={busy} data-testid="register-submit">
                {busy ? <><span className="pf-spin" /> Creating account…</> : <><Zap size={14} /> Create account <ArrowRight size={14} /></>}
              </button>
            </form>

            <div style={{ marginTop: 18, fontSize: 13, color: "var(--pf-text-muted)" }}>
              Already have an account? <Link to="/login" className="pf-link" data-testid="register-to-login">Sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
